import React, { useEffect, useMemo, useState } from "react";
import iconScreenShare from "assets/ScreenShareIcon.svg?url";
import iconScreenShareBlue from "assets/ScreenShareIconBlue.svg?url";
import iconPauseInternet from "assets/IconPauseInternet.svg?url";
import { userDisplayText } from "../../utils/userUtils";
import { UserDetail } from "stores/classroomStore_I";
import {
	Tooltip,
	Flex,
	Checkbox,
	Box,
	Image,
	Button,
	Icon,
	Modal,
	ModalCloseButton,
	ModalContent,
	ModalHeader,
	ModalOverlay,
	Table,
	Tbody,
	Td,
	Text,
	Tr,
} from "@chakra-ui/react";
import { colours } from "utils/themeUtils";

interface ScreenshareModalProps {
	usersSelectedCallback: (users: string[]) => Promise<void>;
	endScreenshareCallback: () => void;
	open: boolean;
	close: () => void;
	screenshareOngoing: boolean;
	allUsers: UserDetail[];
	initialUsers: string[];
	lockedUsers: string[];
}

const ScreenshareModal = ({
	usersSelectedCallback,
	endScreenshareCallback,
	open,
	close,
	allUsers,
	initialUsers,
	screenshareOngoing,
	lockedUsers,
}: ScreenshareModalProps): React.ReactElement => {
	const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
	const users = useMemo(() => [...allUsers], [allUsers]);
	const allSelectedUsers = useMemo(() => [...selectedUsers, ...initialUsers], [selectedUsers, initialUsers]);
	const [loading, setLoading] = useState<boolean>(false);
	const [allStudentsSelected, setAllStudentsSelected] = useState<boolean>(true);

	const canSubmit = useMemo(() => {
		// are there any new users added
		return selectedUsers.filter((user) => !initialUsers.includes(user)).length > 0;
	}, [selectedUsers, initialUsers]);

	useEffect(() => {
		if (!open) {
			selectAllUsers();
			setAllStudentsSelected(true);
		}
	}, [open]);

	const isScreenshareAvailableForUser = (username: string): boolean => {
		// current users are the initial users that are selected, these cannot be deselected
		const isAlreadyScreenSharing = initialUsers.includes(username);
		const paused = lockedUsers.includes(username);
		return !paused && !isAlreadyScreenSharing;
	};

	const selectAllUsers = () => {
		const allUsernames = users.map((u) => u["username"]).filter((u) => isScreenshareAvailableForUser(u));
		setSelectedUsers([...allUsernames]);
	};

	const toggleSelectAllUsers = () => {
		if (allStudentsSelected) {
			setSelectedUsers([]);
		} else {
			selectAllUsers();
		}
		setAllStudentsSelected(!allStudentsSelected);
	};

	const userItem = (user: UserDetail) => {
		const username: string = user["username"];
		const isChecked = allSelectedUsers.includes(username);

		// current users are the initial users that are selected, these cannot be deselected
		const isAlreadyScreenSharing = initialUsers.includes(username);
		const paused = lockedUsers.includes(username);

		const checkbox = (
			<Checkbox
				data-testid={`screenshare-checkbox-${username}`}
				isChecked={isChecked}
				isDisabled={paused || isAlreadyScreenSharing}
				onChange={() => {
					if (!isChecked) {
						setSelectedUsers([...selectedUsers, username]);
						if (selectedUsers.length + 1 === users.length) {
							setAllStudentsSelected(true);
						}
					} else {
						const newSelectedUsers = selectedUsers.filter((u) => u !== username);
						setSelectedUsers(newSelectedUsers);
						setAllStudentsSelected(false);
					}
				}}
			>
				{userDisplayText(user)}
			</Checkbox>
		);

		return (
			<Td key={user["id"]} padding={"none"}>
				<Flex padding={12} maxWidth={"250px"} flexDirection={"row"}>
					<Box alignSelf="flex-start">
						{paused ? (
							<Tooltip placement="top" variant={"dark"} label={"Screen can not be shared with Paused students"}>
								<Box>{checkbox}</Box>
							</Tooltip>
						) : (
							checkbox
						)}
					</Box>
					{isAlreadyScreenSharing && <Image display="flex" src={paused ? iconScreenShare : iconScreenShareBlue} ml={"auto"} boxSize={5} />}
					{!isAlreadyScreenSharing && paused && <Image display="flex" src={iconPauseInternet} ml={"auto"} boxSize={5} opacity={0.5} />}
				</Flex>
			</Td>
		);
	};

	const tableRows = () => {
		const rows = [];
		for (let row = 0; row < users.length; row += 2) {
			rows.push(
				<Tr key={`tr_${row}`}>
					{userItem(users[row])}
					<>{users.length > row + 1 && userItem(users[row + 1])}</>
				</Tr>
			);
		}
		return rows;
	};

	return (
		<Modal isOpen={open} onClose={close} motionPreset="slideInRight">
			<ModalOverlay bg="rgba(0, 0, 0, 0.68)" />
			<ModalContent
				maxW="500px"
				height="100vh"
				position="fixed"
				right="0"
				top="0"
				margin="0"
				borderRadius="0"
				display="flex"
				flexDirection="column"
				justifyContent="space-between"
				p="0"
			>
				<Flex px="20px" py="22" bgColor={colours.gray[100]} alignItems="center" justifyContent="space-between">
					<ModalHeader fontFamily="Roboto" fontSize="18px">
						Select students
					</ModalHeader>
					<ModalCloseButton color={colours.neutrals[300]} position="absolute" top="10px" right="10px" />
				</Flex>
				<Flex h="80vh" flex="1" flexDirection="column" justifyContent="flex-start">
					<Box mt={16} ml={16} pb={16}>
						<Checkbox isChecked={allStudentsSelected} onChange={toggleSelectAllUsers}>
							<Text fontFamily="Roboto" fontSize="14px" fontWeight="500">
								All students
							</Text>
						</Checkbox>
					</Box>
					<Flex pb="24" px="24" overflowY="auto">
						<Table variant="striped" width="100%">
							<Tbody>{tableRows()}</Tbody>
						</Table>
					</Flex>
				</Flex>
				<Flex flexDirection={"row"} justifyContent={"flex-end"} justifySelf="end">
					<Flex w="100%" h="72px" px="20px" bgColor={colours.gray[100]} alignItems="center" justifyContent="space-between">
						{screenshareOngoing ? (
							<Button
								variant={"danger"}
								onClick={() => {
									endScreenshareCallback();
								}}
							>
								Stop Sharing
							</Button>
						) : (
							<Flex></Flex>
						)}
						<Button
							isDisabled={!canSubmit}
							variant={"primary"}
							onClick={() => {
								setLoading(true);
								usersSelectedCallback(allSelectedUsers)
									.then(() => {
										setLoading(false);
									})
									.catch((error) => {
										console.error(`Error selecting users`, error);
										setLoading(false);
									});
							}}
							isLoading={loading}
							rightIcon={
								<Icon>
									<i className="fa fa-arrow-right" />
								</Icon>
							}
						>
							{screenshareOngoing ? "Add to Screen Share" : "Select what to share"}
						</Button>
					</Flex>
				</Flex>
			</ModalContent>
		</Modal>
	);
};

export default ScreenshareModal;
